"use client";

import EventEmitter from "services/EventEmitter";
import LocalStorageService from "services/LocalStorageService";
import { Item } from "types/Item";
import History from "./api/History";
import Products from "./api/Products";
import { v4 as uuidv4 } from "uuid";

export type HistoryItem = Item & { date: number; origin: "scan" | "catalog" };
export default class HistoryService {
	public static readonly instance = new this();
	protected readonly event = new EventEmitter();
	private availableProducts: Item[] = [];

	private constructor() {
		// Fetch and store available products
		this.refreshAvailableProducts();
	}

	/**
	 * Refresh the available products cache
	 */
	private async refreshAvailableProducts() {
		try {
			const response = await Products.getInstance().findAll();
			if (response) {
				//@ts-ignore
				this.availableProducts = response;
			}
		} catch (error) {
			console.error("Failed to refresh available products:", error);
		}
	}

	/**
	 * Get all history items that are scanned and currently available
	 */
	public async get(): Promise<HistoryItem[]> {
		// Refresh available products list to ensure we have the latest data
		await this.refreshAvailableProducts();

		// Get all history items with "scan" origin
		const historyItems =
			LocalStorageService.getInstance()
				.items.history.get()
				?.filter((item) => item.origin === "scan") ?? [];

		// Filter to only include items that are currently available
		const availableHistoryItems = historyItems.filter((historyItem) => {
			// Find the corresponding product in the current product list
			const currentProduct = this.availableProducts.find((product) => product.sku === historyItem.sku);

			// Only include if the product exists and is available
			return currentProduct && currentProduct.disponibilite !== "Epuisé";
		});

		// Sort by date (newest first)
		return availableHistoryItems.sort((itemA, itemB) => {
			return itemA.date < itemB.date ? 1 : itemA.date > itemB.date ? -1 : 0;
		});
	}

	/**
	 * Legacy synchronous version for backward compatibility
	 * @deprecated Use the async get() method instead
	 */
	public getSync(): HistoryItem[] {
		const historyItems =
			LocalStorageService.getInstance()
				.items.history.get()
				?.filter((item) => item.origin === "scan") ?? [];

		// Filter based on cached available products
		const availableHistoryItems = historyItems.filter((historyItem) => {
			const currentProduct = this.availableProducts.find((product) => product.sku === historyItem.sku);
			return currentProduct && currentProduct.disponibilite !== "Epuisé";
		});

		return availableHistoryItems.sort((itemA, itemB) => {
			return itemA.date < itemB.date ? 1 : itemA.date > itemB.date ? -1 : 0;
		});
	}

	public add(item: Item, origin: "scan" | "catalog") {
		// Use sync version for adding items to maintain backward compatibility
		const historyItems = this.getSync();
		// if item is the same than the last one, do nothing
		if (historyItems[0]?.sku === item.sku) {
			return;
		}

		// Get all history items from localStorage (including non-scan origins)
		const allHistoryItems = LocalStorageService.getInstance().items.history.get() ?? [];
		allHistoryItems.push({ ...item, date: Date.now(), origin });
		LocalStorageService.getInstance().items.history.set(allHistoryItems);

		let sessionId = LocalStorageService.getInstance().session.get();
		if (!sessionId) {
			sessionId = uuidv4();
			LocalStorageService.getInstance().session.set(sessionId);
		}

		History.getInstance().create(item, sessionId, origin);
		this.event.emit("change", this.getSync());
	}

	public remote(sku: string) {
		const allHistoryItems = LocalStorageService.getInstance().items.history.get() ?? [];
		const updatedHistoryItems = allHistoryItems.filter((item) => item.sku !== sku);
		LocalStorageService.getInstance().items.history.set(updatedHistoryItems);
		this.event.emit("change", this.getSync());
	}

	public clear() {
		LocalStorageService.getInstance().items.history.delete();
		this.event.emit("change", this.getSync());
	}

	public onChange(callback: (items: HistoryItem[]) => void) {
		this.event.on("change", callback);
		return () => this.event.off("change", callback);
	}
}
